import {useTheme} from "@emotion/react";
import B5SliderImages from "@/components/sections/B5SliderImages";
import Reveal from "@/elements/Reveal";
import {Text} from "@/elements/text";
import WebpPicture from "@/elements/WebpPicture";
import type {SectionImages} from "@/generated/api";
import SectionWrapper from "@/layouts/SectionWrapper";
import {getCardImageSizes, getResponsiveImage} from "@/utils/cardImage";
import {imageOf} from "@/utils/index";
import {Images, ImagesWrapper, SingleImage, TwoColumsWrapper, Wrapper} from "./styles";

interface B4ImagesProps {
  sectionData: SectionImages;
  isFirstSection?: boolean;
  isLastSection?: boolean;
  id?: string;
}

const B4Images = ({sectionData, isFirstSection, isLastSection, id}: B4ImagesProps) => {
  const {images = [], bg_color} = sectionData;
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;
  // Only the single image has a layout that is described (several images sit side by side or in a slider)
  const singleImage = images.length <= 1 ? imageOf(images[0]?.image) : undefined;
  const singleSizes = getCardImageSizes({
    image: singleImage,
    isDestinationCard: false,
    layout: "bodyImage",
  });
  const responsive =
    singleSizes && singleImage?.srcset
      ? {srcSet: singleImage.srcset, sizes: singleSizes}
      : {};

  // A section with no image has nothing to show (it used to throw on `images[0]`)
  if (images.length === 0) return null;

  return (
    <SectionWrapper
      id={id}
      backgroundColor={bg_color}
      isFirstSection={isFirstSection}
      isLastSection={isLastSection}
      styles={{paddingInline: "0px important!"}}
      innerWrapperStyles={{paddingInline: "0px important!"}}
    >
      <ImagesWrapper>
        {images.length <= 1 ? (
          <div style={{marginInline: isMobile ? 10 : 0}}>
            <Reveal from={{transform: "scale(0.9)"}} to={{transform: "scale(1)"}}>
              <WebpPicture webp={singleImage?.webp} sizes={responsive.sizes}>
                <SingleImage
                  src={singleImage?.url}
                  alt={singleImage?.alt || "single image"}
                  title={singleImage?.title || "single image"}
                  {...responsive}
                  loading="lazy"
                />
              </WebpPicture>
              <Text styles={{marginTop: 8, color: "#737373"}} type="textSmallRegular">
                {singleImage?.caption}
              </Text>
            </Reveal>
          </div>
        ) : isMobile ? (
          <B5SliderImages sectionData={{images, bg_color}} />
        ) : (
          <TwoColumsWrapper>
            {images.map((item, i) => {
              const image = imageOf(item.image);
              const pair = getResponsiveImage({image, layout: "bodyImagePair"});
              return (
                <Wrapper key={i}>
                  <WebpPicture webp={image?.webp} sizes={pair.sizes}>
                    <Images
                      src={image?.url}
                      alt={image?.alt || "img"}
                      title={image?.title || "img"}
                      {...pair}
                      loading="lazy"
                    />
                  </WebpPicture>
                  <Text styles={{marginTop: 8, color: "#737373"}} type="textSmallRegular">
                    {image?.caption || ""}
                  </Text>
                </Wrapper>
              );
            })}
          </TwoColumsWrapper>
        )}
      </ImagesWrapper>
    </SectionWrapper>
  );
};

export default B4Images;
